import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDateTime } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import { segmentedControlClass, segmentedOptionClass } from '../dashboard/themeHelpers';
import { useSystemHealthSummary } from './useSystemHealthSummary';
import EmailAccountHealthCard from './components/EmailAccountHealthCard';
import OnlineUsersCard from './components/OnlineUsersCard';
import SectionCard from './components/SectionCard';
import SyncStatsCard from './components/SyncStatsCard';
import SyncLogTimeline from './components/SyncLogTimeline';
import TodayUserLoginsCard from './components/TodayUserLoginsCard';
import UserHealthHero from './components/UserHealthHero';
import { systemHealthAdminPath } from './utils';
import { zoneLinkClass } from './theme';

const POLL_INTERVAL_MS = 30_000;

const SystemHealthStatusPage: React.FC = () => {
  const { colors } = useTheme();
  const { can } = useAuth();
  const canManageEmailSync = can('system_health', 'edit');
  const [liveEnabled, setLiveEnabled] = useState(true);

  const { data, isLoading, error, mutate, isValidating } = useSystemHealthSummary({
    refreshInterval: liveEnabled ? POLL_INTERVAL_MS : 0,
    revalidateOnFocus: liveEnabled,
    dedupingInterval: liveEnabled ? 0 : undefined,
  });

  const collectedLabel = useMemo(() => {
    if (!data?.collectedAt) return null;
    return formatDateTime(data.collectedAt);
  }, [data?.collectedAt]);

  const accountWarnings = useMemo(() => {
    if (!data?.emailSync.accounts) return [];
    return data.emailSync.accounts
      .filter((a) => a.health === 'warning' || a.health === 'error' || a.health === 'paused')
      .map((a) => `${a.displayName || a.emailAddress}: ${a.healthMessage}`);
  }, [data?.emailSync.accounts]);

  if (error) {
    return (
      <div className={cn('w-full max-w-[90rem] mx-auto px-4 py-6 sm:px-6', colors.background)}>
        <div className={cn('rounded-2xl border p-6 text-center max-w-lg mx-auto', colors.surface, colors.border)}>
          <h2 className={cn('text-lg font-semibold', colors.text)}>Unable to load sync status</h2>
          <p className={cn('text-sm mt-2', colors.textMuted)}>
            You may not have permission to view this page.
          </p>
          <button
            type="button"
            onClick={() => void mutate()}
            className={cn('mt-4 text-sm font-medium px-4 py-2 rounded-lg border', colors.border, colors.text)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full max-w-[90rem] mx-auto px-4 py-6 sm:px-6 flex flex-col gap-6', colors.background)}>
      <div className={cn('flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-4 p-3 rounded-xl border', colors.surface, colors.border)}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
          <p className={cn('text-sm', colors.textMuted)}>
            Email sync status — account health and recent activity (read-only).
          </p>
          {collectedLabel && (
            <span className={cn('text-xs sm:ml-auto tabular-nums', colors.textMuted)}>
              {isValidating ? 'Updating… · ' : ''}
              Updated: {collectedLabel}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-4">
          <div className={segmentedControlClass(colors, true)}>
            <button
              type="button"
              onClick={() => setLiveEnabled(true)}
              className={segmentedOptionClass(colors, liveEnabled, 'soft', true)}
            >
              Live
            </button>
            <button
              type="button"
              onClick={() => setLiveEnabled(false)}
              className={segmentedOptionClass(colors, !liveEnabled, 'soft', true)}
            >
              Paused
            </button>
          </div>
          <button
            type="button"
            onClick={() => void mutate()}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              colors.primary,
              colors.primaryText,
            )}
          >
            <svg
              className={cn('w-4 h-4', isValidating && 'animate-spin')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Refresh
          </button>
          {canManageEmailSync && (
            <Link
              to={systemHealthAdminPath()}
              className={cn(
                'text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors',
                colors.border,
                colors.text,
                colors.interactiveHover,
              )}
            >
              Full system health
            </Link>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-6 w-full">
        {isLoading && !data ? (
          <div className="space-y-4">
            <div className={cn('h-28 rounded-2xl animate-pulse', colors.surfaceHover)} />
            <div className={cn('h-40 rounded-xl animate-pulse', colors.surfaceHover)} />
          </div>
        ) : data ? (
          <>
            <UserHealthHero data={data} />

            {accountWarnings.length > 0 && (
              <div className={cn('rounded-xl border px-4 py-3', colors.warning, colors.border)}>
                <p className="font-semibold text-sm mb-2">Attention needed</p>
                <ul className="space-y-1.5 text-sm">
                  {accountWarnings.map((w) => (
                    <li key={w} className="flex gap-2">
                      <span aria-hidden>•</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {data.onlineUsers?.users && (
                <OnlineUsersCard
                  intensity="user"
                  onlineUsers={{
                    uniqueUsers: data.onlineUsers.uniqueUsers,
                    totalSockets: data.onlineUsers.totalSockets,
                    users: data.onlineUsers.users,
                  }}
                />
              )}

              {data.syncStats && <SyncStatsCard syncStats={data.syncStats} intensity="user" />}
            </div>

            {data.todayUserSessions && (
              <TodayUserLoginsCard initialTodaySessions={data.todayUserSessions} intensity="user" />
            )}

            <SectionCard
              zone="mail"
              intensity="user"
              title="Email accounts"
              subtitle="Sync health per account — contact an admin to pause or resume sync"
              action={
                <Link to="/emails" className={zoneLinkClass('mail')}>
                  Open mail
                </Link>
              }
            >
              {data.emailSync.accounts.length === 0 ? (
                <p className={cn('text-sm py-6 text-center', colors.textMuted)}>No email accounts configured.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.emailSync.accounts.map((account) => (
                    <EmailAccountHealthCard
                      key={account.id}
                      account={account}
                      canManageSync={false}
                    />
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard zone="mail" intensity="user" title="Recent sync runs" subtitle="Latest activity across all accounts">
              <SyncLogTimeline logs={data.emailSync.recentLogs} />
            </SectionCard>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default SystemHealthStatusPage;
